import { useEffect, useState } from "react";
import { ActionIcon, Badge, Container, Group, Loader, Switch } from "@mantine/core";
import { IconArchiveOff, IconPencil, IconPlus, IconTrash } from "@tabler/icons-react";
import { DataTable } from "mantine-datatable";
import { notifications } from "@mantine/notifications";
import { useNavigate } from "react-router-dom";
import HttpClient from "../helpers/HttpClient";
import { Article } from "../types/article.type";
import { ArticleRemoveDialog } from "../components/ArticleRemoveDialog";
import { useLanguage } from "../providers/LanguageContext";

function ArticleList() {
  const { t } = useLanguage();
  const [loading, setLoading] = useState(true);
  const [articles, setArticles] = useState<Article[]>([]);
  const [removeTarget, setRemoveTarget] = useState<{ id: number; name: string } | null>(null);
  const [showArchived, setShowArchived] = useState(false);
  const navigate = useNavigate();

  const fetchData = async () => {
    setLoading(true);
    try {
      const result = await new HttpClient<Article>("/article").getAll(showArchived ? { archived: "true" } : undefined);
      setArticles(result);
    } catch {
      notifications.show({ message: t.article.loadFailed, color: "red" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, [showArchived]);

  const restore = async (article: Article) => {
    try {
      await new HttpClient(`/article/${article.id}`).postAction("restore");
      notifications.show({ message: t.article.restored, color: "green" });
      fetchData();
    } catch {
      notifications.show({ message: t.article.saveFailed, color: "red" });
    }
  };

  return (
    <Container fluid pb={10} mt={10}>
      <ArticleRemoveDialog
        article={removeTarget}
        basePath="/article"
        onClose={() => setRemoveTarget(null)}
        onDone={() => fetchData()}
      />
      <Group gap={25} visibleFrom="xs">
        <h1>{t.article.title}</h1>
        <ActionIcon
          variant="filled"
          size="xl"
          radius="xl"
          aria-label={t.article.newArticle}
          onClick={() => navigate("/article/create")}
        >
          <IconPlus style={{ width: "70%", height: "70%" }} stroke={1.5} />
        </ActionIcon>
      </Group>
      <Switch
        label={t.article.showArchived}
        checked={showArchived}
        onChange={(e) => setShowArchived(e.currentTarget.checked)}
        mb="sm"
      />
      {loading ? (
        <Loader size="md" />
      ) : (
        <DataTable
          withTableBorder
          borderRadius="sm"
          withColumnBorders
          striped
          highlightOnHover
          records={articles}
          idAccessor="id"
          columns={[
            { accessor: "name", title: t.article.colName },
            { accessor: "description", title: t.article.colDescription },
            {
              accessor: "category",
              title: t.article.colCategory,
              width: "12%",
              render: (a) => a.category?.name ?? "–",
            },
            {
              accessor: "unitPrice",
              title: t.article.colUnitPrice,
              width: "15%",
              render: (a) =>
                a.unitPrice != null
                  ? `€ ${Number(a.unitPrice).toFixed(2).replace(".", ",")}`
                  : "–",
            },
            {
              accessor: "isRestaurantArticle",
              title: "Restaurant",
              width: "10%",
              render: (a) => (
                <Group gap={4}>
                  {a.isRestaurantArticle && <Badge color="teal" size="sm">✓</Badge>}
                  {a.isArchived && <Badge color="orange" variant="light" size="sm">{t.article.archivedBadge}</Badge>}
                </Group>
              ),
            },
            {
              accessor: "actions",
              title: "",
              width: "8%",
              render: (a) => (
                <Group gap={4} justify="center">
                  <ActionIcon
                    variant="light"
                    color="blue"
                    onClick={() => navigate(`/article/edit/${a.id}`)}
                    aria-label={t.common.edit}
                  >
                    <IconPencil size={18} />
                  </ActionIcon>
                  {a.isArchived ? (
                    <ActionIcon
                      variant="light"
                      color="orange"
                      onClick={() => restore(a)}
                      aria-label={t.article.restore}
                      title={t.article.restore}
                    >
                      <IconArchiveOff size={18} />
                    </ActionIcon>
                  ) : (
                    <ActionIcon
                      variant="light"
                      color="red"
                      onClick={() => setRemoveTarget({ id: Number(a.id), name: a.name })}
                      aria-label={t.article.deleteOrArchive}
                      title={t.article.deleteOrArchive}
                    >
                      <IconTrash size={18} />
                    </ActionIcon>
                  )}
                </Group>
              ),
            },
          ]}
        />
      )}
    </Container>
  );
}

export default ArticleList;
